/**
 * Copy and error handling for the organisation-funded option in the spending
 * approval modal. The organisation is always admin funded: members are never
 * offered a top-up, only told which limit blocks the amount and when it resets.
 */
import type {
  OrganisationChargeErrorCode,
  OrganisationSpendBlocker,
  OrganisationSpendReadiness,
} from '@dreamai/types';

export function isOrganisationCard(
  selection: { cardType: string } | undefined,
): boolean {
  return selection?.cardType === 'organisation';
}

export function formatOrganisationAmount(
  amount: number,
  currency: string,
): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
}

export function formatResetDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

export function organisationBlockerMessage(
  blocker: OrganisationSpendBlocker,
  readiness: Pick<OrganisationSpendReadiness, 'organisationName' | 'currency'>,
): string {
  const money = (n: number) => formatOrganisationAmount(n, readiness.currency);
  switch (blocker.code) {
    case 'card_disabled':
      return `${readiness.organisationName} has not enabled organisation-funded purchases for you.`;
    case 'billing_not_ready':
      return `${readiness.organisationName} has no payment method on file. Ask an organisation admin to set one up.`;
    case 'fx_unavailable':
      return `We could not convert ${blocker.currency} into ${readiness.currency} just now, so this cannot be checked against your organisation's limits. Try again shortly.`;
    case 'over_per_transaction_cap':
      return `This is above your organisation's per-purchase limit of ${money(blocker.cap)}.`;
    case 'over_cycle_cap':
      return blocker.remaining > 0
        ? `You have ${money(blocker.remaining)} of your ${money(blocker.cap)} organisation limit left until ${formatResetDate(blocker.resetsAt)}.`
        : `You have used your ${money(blocker.cap)} organisation limit for this billing cycle. It resets on ${formatResetDate(blocker.resetsAt)}.`;
  }
}

/** Reason the organisation option is unavailable, or undefined when it is. */
export function organisationUnavailableReason(
  readiness: OrganisationSpendReadiness,
): string | undefined {
  if (readiness.available) return undefined;
  const first = readiness.blockers[0];
  if (first) return organisationBlockerMessage(first, readiness);
  return (
    readiness.blockedReason ||
    `${readiness.organisationName} cannot fund this purchase right now.`
  );
}

/**
 * In-card line for a member who is over a spend limit their admin set, or
 * undefined when the organisation is blocked for any other reason.
 */
export function organisationLimitHint(
  readiness: OrganisationSpendReadiness,
): string | undefined {
  if (readiness.available) return undefined;
  const blocker = readiness.blockers[0];
  switch (blocker?.code) {
    case 'over_cycle_cap':
      return blocker.remaining > 0
        ? `You only have ${formatOrganisationAmount(blocker.remaining, readiness.currency)} of monthly spend left. Ask your admin to increase it.`
        : "You've hit your monthly spend limit. Ask your admin to increase it.";
    case 'over_per_transaction_cap':
      return `Your admin has set a spend limit of ${formatOrganisationAmount(blocker.cap, readiness.currency)} per transaction. Ask them to increase it.`;
    default:
      return undefined;
  }
}

/** Right-hand usage label for the organisation row: "Used £350/£1,500". */
export function organisationUsageLabel(
  readiness: OrganisationSpendReadiness,
): string {
  const used = formatOrganisationAmount(
    readiness.cycleCommitted,
    readiness.currency,
  );
  if (readiness.cycleCap == null) return `Used ${used}`;
  return `Used ${used}/${formatOrganisationAmount(readiness.cycleCap, readiness.currency)}`;
}

/** Helper shown under the selected organisation row. */
export function organisationAllowanceNote(
  readiness: OrganisationSpendReadiness,
): string | undefined {
  const parts: string[] = [];
  if (readiness.cycleCap != null) {
    parts.push(
      `You've used ${formatOrganisationAmount(readiness.cycleCommitted, readiness.currency)} of your ${formatOrganisationAmount(readiness.cycleCap, readiness.currency)} monthly allowance.`,
    );
  }
  if (readiness.perTransactionCap != null) {
    parts.push(
      `Your admin has set a transaction limit of ${formatOrganisationAmount(readiness.perTransactionCap, readiness.currency)} on this card.`,
    );
  }
  return parts.length ? parts.join(' ') : undefined;
}

/** Short right-hand label for the organisation row when it is available. */
export function organisationHeadroomLabel(
  readiness: OrganisationSpendReadiness,
): string {
  if (readiness.cycleRemaining == null) return 'No spending limit';
  return `${formatOrganisationAmount(readiness.cycleRemaining, readiness.currency)} left until ${formatResetDate(readiness.cycleResetsAt)}`;
}

const CHARGE_ERROR_CODES: ReadonlySet<string> =
  new Set<OrganisationChargeErrorCode>([
    'not_eligible',
    'card_disabled',
    'billing_not_ready',
    'fx_unavailable',
    'over_per_transaction_cap',
    'over_cycle_cap',
    'org_charge_failed',
  ]);

interface CodedErrorBody {
  error?: string;
  message?: string;
  code?: string;
}

function errorBody(err: unknown): CodedErrorBody | undefined {
  const axiosErr = err as { response?: { data?: CodedErrorBody } };
  return axiosErr?.response?.data;
}

export function organisationChargeErrorCode(
  err: unknown,
): OrganisationChargeErrorCode | undefined {
  const code = errorBody(err)?.code;
  return code && CHARGE_ERROR_CODES.has(code)
    ? (code as OrganisationChargeErrorCode)
    : undefined;
}

export function isOrganisationChargeError(err: unknown): boolean {
  return organisationChargeErrorCode(err) !== undefined;
}

/**
 * Copy for a failed organisation-funded approval. A declined charge leaves no
 * approval and no card behind, so the message says so rather than offering a
 * retry with another funding source.
 */
export function organisationChargeErrorMessage(
  err: unknown,
  organisationName: string,
): string {
  const code = organisationChargeErrorCode(err);
  const serverMessage = errorBody(err)?.error || errorBody(err)?.message;
  switch (code) {
    case 'org_charge_failed':
      return `${organisationName}'s payment was declined, so nothing has been approved. Ask an organisation admin to check the organisation's payment method, or pay another way.`;
    case 'not_eligible':
      return `You are no longer eligible for organisation-funded purchases from ${organisationName}.`;
    case 'card_disabled':
    case 'billing_not_ready':
    case 'fx_unavailable':
    case 'over_per_transaction_cap':
    case 'over_cycle_cap':
      return (
        serverMessage ||
        `${organisationName} cannot fund this purchase right now.`
      );
    default:
      return (
        serverMessage ||
        `Charging ${organisationName} failed, so nothing has been approved. Please try again.`
      );
  }
}

export interface OrganisationChargeFailure {
  title: string;
  body: string;
}

const RETRY_HINT = "You can retry if you're sure this didn't go through.";

/**
 * Copy for an organisation charge that was declined or never answered. A
 * charge the organisation blocked (limits, card off) has no failure copy: the
 * server's reason is shown instead.
 */
export function organisationChargeFailure(
  err: unknown,
  organisationName: string,
): OrganisationChargeFailure | undefined {
  const code = organisationChargeErrorCode(err);
  if (code === 'org_charge_failed') {
    return {
      title: `${organisationName}'s payment was declined, so nothing has been approved.`,
      body: `Ask an organisation admin to check the organisation's payment method, or pay another way. ${RETRY_HINT}`,
    };
  }
  if (code === undefined) {
    return {
      title: 'Payment failed, so nothing has been approved.',
      body: RETRY_HINT,
    };
  }
  return undefined;
}
